"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { ProgressTracker, CompactProgressTracker } from "./ProgressTracker";
import { 
  Play, 
  Pause, 
  RotateCcw, 
  ArrowRight, 
  ArrowLeft,
  Monitor,
  Smartphone
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface ProgressDemoProps {
  onBack?: () => void;
  onShowTerms?: () => void;
}

export function ProgressDemo({ onBack, onShowTerms }: ProgressDemoProps) {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(false);
  const [selectedVariant, setSelectedVariant] = React.useState<"horizontal" | "vertical">("horizontal");
  const [selectedSize, setSelectedSize] = React.useState<"sm" | "md" | "lg">("md");

  const totalSteps = 6;

  // Auto-play functionality
  React.useEffect(() => {
    if (isAutoPlaying && currentStep < totalSteps) {
      const timer = setTimeout(() => {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      }, 2000);
      return () => clearTimeout(timer);
    } else if (currentStep >= totalSteps) {
      setIsAutoPlaying(false);
    }
  }, [isAutoPlaying, currentStep, totalSteps]);

  const handleToggleAutoPlay = () => {
    if (currentStep >= totalSteps) {
      setCurrentStep(0);
    }
    setIsAutoPlaying(!isAutoPlaying);
  };

  const handleReset = () => {
    setCurrentStep(0);
    setIsAutoPlaying(false);
  };

  const handleNext = () => {
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    setIsAutoPlaying(false);
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
    setIsAutoPlaying(false);
  };

  return (
    <div className="h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 bg-indigo-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-radial from-indigo-100/10 to-transparent rounded-full" />
      </div>

      {/* Fixed Header with Compact Progress */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 bg-white/50 backdrop-blur-sm border-b border-gray-200/50 flex-shrink-0"
      >
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
            <div className="hidden md:block">
              <CompactProgressTracker 
                currentStep={currentStep} 
                totalSteps={totalSteps}
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="md:hidden">
              <CompactProgressTracker 
                currentStep={currentStep} 
                totalSteps={totalSteps}
              />
            </div>
            {onBack && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Demo
              </Button>
            )}
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl mx-auto flex items-center justify-center mb-6">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            
            <h1 className="text-gray-900 mb-4">
              Progress Tracker Demo 📊
            </h1>
            
            <p className="text-gray-600 max-w-2xl mx-auto">
              Experience how candidates can track their onboarding progress in real-time. 
              Watch the progress bar update as they complete each step of their journey.
            </p>
          </motion.div>

          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20 mb-8"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Playback Controls */}
              <div className="flex items-center space-x-3">
                <Button
                  onClick={handleToggleAutoPlay}
                  variant={isAutoPlaying ? "destructive" : "default"}
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  {isAutoPlaying ? (
                    <>
                      <Pause className="w-4 h-4" />
                      <span>Pause</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      <span>Auto Play</span>
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleReset}
                  variant="outline"
                  size="sm"
                  className="flex items-center space-x-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reset</span>
                </Button>

                <div className="flex items-center space-x-2">
                  <Button
                    onClick={handlePrevious}
                    variant="outline"
                    size="sm"
                    disabled={currentStep <= 0}
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  
                  <Button
                    onClick={handleNext}
                    variant="outline"
                    size="sm"
                    disabled={currentStep >= totalSteps}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Display Options */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Layout:</span>
                  <Button
                    onClick={() => setSelectedVariant("horizontal")}
                    variant={selectedVariant === "horizontal" ? "default" : "outline"}
                    size="sm"
                  >
                    <Monitor className="w-4 h-4 mr-1" />
                    Horizontal
                  </Button>
                  <Button
                    onClick={() => setSelectedVariant("vertical")}
                    variant={selectedVariant === "vertical" ? "default" : "outline"}
                    size="sm"
                  >
                    <Smartphone className="w-4 h-4 mr-1" />
                    Vertical
                  </Button>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Size:</span>
                  <select
                    value={selectedSize}
                    onChange={(e) => setSelectedSize(e.target.value as "sm" | "md" | "lg")}
                    className="text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                  >
                    <option value="sm">Small</option>
                    <option value="md">Medium</option>
                    <option value="lg">Large</option>
                  </select>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Progress Tracker Display */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20 mb-8"
          >
            <h3 className="text-gray-900 mb-8 text-center">
              Onboarding Progress Tracker
            </h3>
            
            <div className={`
              flex items-center justify-center
              ${selectedVariant === "vertical" ? "min-h-[600px]" : "min-h-[200px]"}
            `}>
              <div className={`
                ${selectedVariant === "vertical" ? "max-w-md" : "w-full max-w-5xl"}
                ${selectedVariant === "horizontal" ? "overflow-x-auto" : ""}
              `}>
                <ProgressTracker
                  currentStep={currentStep}
                  variant={selectedVariant}
                  size={selectedSize}
                  className={selectedVariant === "vertical" ? "mx-auto" : ""}
                />
              </div>
            </div>
          </motion.div>

          {/* Current Step Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
          >
            <div className="text-center">
              <h4 className="text-gray-900 mb-2">
                Current Status
              </h4>
              <p className="text-gray-600 mb-4">
                {currentStep === 0 && "Ready to begin your onboarding journey!"}
                {currentStep === 1 && "Verifying your identity with OTP..."}
                {currentStep === 2 && "Uploading and reviewing documents..."}
                {currentStep === 3 && "Reviewing and signing appointment letter..."}
                {currentStep === 4 && "HR team reviewing your submission..."}
                {currentStep === 5 && "IT team setting up your access..."}
                {currentStep >= 6 && "Welcome to the team! 🎉"}
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                <span>Step {Math.min(currentStep, totalSteps)} of {totalSteps}</span>
                <span>•</span>
                <span>{Math.round((Math.min(currentStep, totalSteps) / totalSteps) * 100)}% Complete</span>
              </div>
            </div>
          </motion.div>

          {/* Usage Examples */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-8 bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/20"
          >
            <h4 className="text-gray-900 mb-4 text-center">Implementation Examples</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="text-gray-800 mb-2">Email Tracking Link</h5>
                <p className="text-sm text-gray-600 mb-3">
                  Candidates receive an email with a tracking link to monitor their progress.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <code className="text-xs text-gray-700">
                    https://dayone.openturf.com/track/ABC123
                  </code>
                </div>
              </div>
              
              <div>
                <h5 className="text-gray-800 mb-2">Dashboard Integration</h5>
                <p className="text-sm text-gray-600 mb-3">
                  Progress tracker appears in the candidate dashboard and during each step.
                </p>
                <div className="bg-gray-50 rounded-lg p-3 border">
                  <code className="text-xs text-gray-700">
                    &lt;ProgressTracker currentStep={2} /&gt;
                  </code>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm flex-shrink-0"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between w-full">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <button 
              onClick={onShowTerms}
              className="text-purple-600 hover:text-purple-700 hover:underline transition-colors"
            >
              Terms & Conditions
            </button>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}